import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, ArrowRight } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from 'recharts';
import { formatMoney } from '@/lib/format';

// Phase 57 — Shared building blocks for the detail pages.

export function DetailHeader({ icon: Icon, title, subtitle, period, onPeriodChange, accent }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        data-testid="detail-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>
      <div className="flex items-start gap-3">
        {Icon && (
          <div
            className="h-12 w-12 rounded-2xl grid place-items-center shrink-0"
            style={accent ? { background: `${accent}22`, color: accent } : undefined}
            data-testid="detail-icon"
          >
            <Icon className="h-5 w-5" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-xl sm:text-2xl font-semibold truncate" data-testid="detail-title">{title}</h1>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-0.5" data-testid="detail-subtitle">{subtitle}</p>
          )}
        </div>
      </div>
      <PeriodChips value={period} onChange={onPeriodChange} />
    </div>
  );
}

const PERIODS = [
  { v: 7, label: '7d' },
  { v: 30, label: '30d' },
  { v: 90, label: '90d' },
  { v: null, label: 'All' },
];

function PeriodChips({ value, onChange }) {
  return (
    <div className="flex items-center gap-1.5" data-testid="detail-period-chips">
      <Calendar className="h-3.5 w-3.5 text-muted-foreground mr-1" />
      {PERIODS.map((p) => {
        const active = value === p.v;
        return (
          <button
            key={p.label}
            type="button"
            onClick={() => onChange(p.v)}
            data-testid={`detail-period-${p.label}`}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
              active
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

export function StatTile({ label, value, sub, testid }) {
  return (
    <Card className="rounded-2xl border-border/70 h-full" data-testid={testid}>
      <CardContent className="p-4 sm:p-5">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1.5 font-display text-xl sm:text-2xl font-semibold tabular-nums">{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{sub}</div>}
      </CardContent>
    </Card>
  );
}

export function TrendChart({ data, currency, accent = 'hsl(var(--primary))', testid }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-48 grid place-items-center text-xs text-muted-foreground border border-dashed border-border/60 rounded-xl" data-testid={`${testid || 'detail-trend'}-empty`}>
        No data in this period yet.
      </div>
    );
  }
  return (
    <div className="h-56" data-testid={testid || 'detail-trend'}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="detailGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
              <stop offset="100%" stopColor={accent} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => formatMoney(v, currency, { compact: true })} />
          <RTooltip
            contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12, fontSize: 12 }}
            formatter={(v) => formatMoney(v, currency)}
          />
          <Area type="monotone" dataKey="value" stroke={accent} strokeWidth={2} fill="url(#detailGrad)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function TopMerchantsCard({ rows, currency }) {
  return (
    <Card className="rounded-2xl border-border/70 h-full" data-testid="detail-top-merchants">
      <CardContent className="p-5">
        <div className="text-sm font-medium mb-3">Top merchants</div>
        {rows.length === 0 ? (
          <div className="text-xs text-muted-foreground">No merchants in this period.</div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.name} className="flex items-center justify-between gap-3" data-testid="detail-merchant-row">
                <div className="min-w-0">
                  <div className="text-sm truncate">{r.name}</div>
                  <div className="text-[11px] text-muted-foreground">{r.count} {r.count === 1 ? 'transaction' : 'transactions'}</div>
                </div>
                <div className="font-display text-sm font-semibold tabular-nums shrink-0">
                  {formatMoney(r.total, currency, { compact: true })}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function BreakdownCard({ title, rows, currency, onRowClick, testid, swatchFn }) {
  return (
    <Card className="rounded-2xl border-border/70 h-full" data-testid={testid}>
      <CardContent className="p-5">
        <div className="text-sm font-medium mb-3">{title}</div>
        {rows.length === 0 ? (
          <div className="text-xs text-muted-foreground">No breakdown for this period.</div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => {
              const inner = (
                <div className="flex items-center justify-between text-xs gap-3">
                  <span className="flex items-center gap-2 min-w-0">
                    {swatchFn && (
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: swatchFn(r.key) }} />
                    )}
                    <span className="truncate">{r.key}</span>
                  </span>
                  <span className="text-muted-foreground tabular-nums shrink-0">
                    {formatMoney(r.total, currency, { compact: true })} · {r.pct.toFixed(0)}%
                  </span>
                </div>
              );
              const bar = (
                <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${r.pct}%`, background: swatchFn ? swatchFn(r.key) : 'hsl(var(--primary))' }} />
                </div>
              );
              return onRowClick ? (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => onRowClick(r.key)}
                  className="block w-full text-left cursor-pointer group"
                  data-testid={`breakdown-row-${r.key}`}
                >
                  <div className="group-hover:opacity-80 transition-opacity">
                    {inner}
                    {bar}
                  </div>
                </button>
              ) : (
                <div key={r.key} data-testid={`breakdown-row-${r.key}`}>
                  {inner}
                  {bar}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function TransactionsTable({ rows, currency, accounts, emptyHint }) {
  const accMap = React.useMemo(() => {
    const m = {};
    (accounts || []).forEach((a) => { m[a.id] = a.name; });
    return m;
  }, [accounts]);

  if (rows.length === 0) {
    return (
      <Card className="rounded-2xl border-border/70">
        <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="detail-tx-empty">
          {emptyHint || 'No transactions in this period.'}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="rounded-2xl border-border/70 overflow-hidden">
      <div className="divide-y divide-border/60" data-testid="detail-tx-list">
        {rows.map((t) => {
          const isIncome = t.type === 'income';
          const isExpense = t.type === 'expense';
          const sign = isIncome ? '+' : isExpense ? '−' : '';
          return (
            <div key={t.id} className="px-4 py-3 flex items-center gap-3" data-testid="detail-tx-row">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate" title={t.description_clean || t.description}>
                  {t.description_clean || t.description || '—'}
                </div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                  <span>{(t.txn_date || '').slice(0, 10)}</span>
                  {t.category && (
                    <>
                      <span aria-hidden>·</span>
                      <Badge variant="secondary" className="rounded-full text-[10px] h-4 px-1.5">{t.category}</Badge>
                    </>
                  )}
                  {t.account_id && accMap[t.account_id] && (
                    <>
                      <span aria-hidden>·</span>
                      <span className="truncate max-w-[160px]">{accMap[t.account_id]}</span>
                    </>
                  )}
                </div>
              </div>
              <div className={`font-display text-sm font-semibold tabular-nums shrink-0 ${isExpense ? 'text-foreground' : isIncome ? 'text-[hsl(var(--success))]' : 'text-muted-foreground'}`}>
                {sign}{formatMoney(Math.abs(Number(t.amount || 0)), t.currency || currency, { compact: false })}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export function GoToFullList({ to, label = 'Open in full Transactions view' }) {
  return (
    <div className="pt-1">
      <Button asChild variant="outline" className="rounded-xl">
        <a href={to} data-testid="detail-go-full-tx">
          {label} <ArrowRight className="h-4 w-4 ml-1.5" />
        </a>
      </Button>
    </div>
  );
}
